import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ChevronRight, Activity, Database, MapPin, DollarSign, CheckCircle, Loader2, AlertCircle, TrendingUp } from 'lucide-react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useSuccessWareMonitor } from '@/lib/hooks';

interface ActivityItem {
  id: string;
  type: 'customer' | 'service_account' | 'location' | 'billing';
  name: string;
  company_id: string;
  timestamp: string;
  action: 'created' | 'updated';
}

const SuccessWareActivityMonitorPage = () => {
  // Use combined hook to fetch all monitoring data
  const { stats, activity: recentActivity, syncStates, isLoading, refetch } = useSuccessWareMonitor();
  
  // Local state for real-time activity feed
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [localStats, setLocalStats] = useState(stats);
  const [isListening, setIsListening] = useState(false);

  // Initialize activity feed with recent data
  useEffect(() => {
    if (recentActivity && recentActivity.length > 0) {
      const items: ActivityItem[] = recentActivity.map(c => ({
        id: c.id,
        type: 'customer' as const,
        name: c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.id,
        company_id: c.company_id || 'unknown',
        timestamp: c.created_at,
        action: 'created' as const
      }));
      setActivity(items);
    }
  }, [recentActivity]);

  // Sync local stats with fetched stats
  useEffect(() => {
    if (stats) {
      setLocalStats(stats);
    }
  }, [stats]);

  // Set up realtime subscriptions
  useEffect(() => {
    let channels: RealtimeChannel[] = [];

    const setupRealtimeSubscriptions = async () => {
      const customersChannel = supabase
        .channel('customers-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sw_customers' }, (payload) => {
          handleRealtimeUpdate('customer', payload);
        })
        .subscribe();

      const serviceAccountsChannel = supabase
        .channel('service-accounts-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sw_service_accounts' }, (payload) => {
          handleRealtimeUpdate('service_account', payload);
        })
        .subscribe();

      const locationsChannel = supabase
        .channel('locations-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sw_locations' }, (payload) => {
          handleRealtimeUpdate('location', payload);
        })
        .subscribe();

      const billingChannel = supabase
        .channel('billing-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sw_billing_customers' }, (payload) => {
          handleRealtimeUpdate('billing', payload);
        })
        .subscribe();

      const syncStateChannel = supabase
        .channel('sync-state-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'sw_sync_state' }, () => {
          // Refetch sync states when they change
          refetch();
        })
        .subscribe();

      channels = [customersChannel, serviceAccountsChannel, locationsChannel, billingChannel, syncStateChannel];
      setIsListening(true);
    };

    setupRealtimeSubscriptions();

    return () => {
      channels.forEach(channel => supabase.removeChannel(channel));
      setIsListening(false);
    };
  }, [refetch]);

  const handleRealtimeUpdate = (type: ActivityItem['type'], payload: any) => {
    const newItem: ActivityItem = {
      id: payload.new?.id || payload.old?.id || Math.random().toString(),
      type,
      name: extractName(type, payload.new || payload.old),
      company_id: payload.new?.company_id || payload.old?.company_id || 'unknown',
      timestamp: new Date().toISOString(),
      action: payload.eventType === 'INSERT' ? 'created' : 'updated'
    };

    setActivity(prev => [newItem, ...prev].slice(0, 100));
    
    // Update stats incrementally for INSERT events
    if (payload.eventType === 'INSERT') {
      setLocalStats(prev => {
        if (!prev) return prev;
        const updated = { ...prev };
        
        switch (type) {
          case 'customer':
            updated.totalCustomers += 1;
            // Check if within last 5 minutes
            updated.recentImports += 1;
            break;
          case 'service_account':
            updated.totalServiceAccounts += 1;
            break;
          case 'location':
            updated.totalLocations += 1;
            break;
        }
        
        return updated;
      });
    }
  };

  const extractName = (type: string, data: any): string => {
    if (!data) return 'Unknown';
    
    switch (type) {
      case 'customer':
        return data.company_name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.id;
      case 'service_account':
        return data.customer_name || data.id;
      case 'location':
        return `${data.address1 || ''}, ${data.city || ''}`.trim() || data.id;
      case 'billing':
        return `${data.first_name || ''} ${data.last_name || ''}`.trim() || data.id;
      default:
        return data.id;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'triggered': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'running': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4" />;
      case 'triggered':
      case 'running': return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'failed': return <AlertCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'customer': return <Activity className="w-4 h-4" />;
      case 'service_account': return <Database className="w-4 h-4" />;
      case 'location': return <MapPin className="w-4 h-4" />;
      case 'billing': return <DollarSign className="w-4 h-4" />;
      default: return null;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading SuccessWare activity...</p>
        </div>
      </div>
    );
  }

  // Use local stats or fallback to fetched stats
  const displayStats = localStats || stats || {
    totalCustomers: 0,
    totalServiceAccounts: 0,
    totalLocations: 0,
    recentImports: 0
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
        <Link to="/integrations" className="hover:text-white">Integrations</Link>
        <ChevronRight className="w-4 h-4" />
        <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-white">Activity Monitor</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Activity Monitor</h1>
          <p className="text-slate-400">Live customer and data import activity</p>
        </div>
        <div className={`px-3 py-1.5 rounded-lg border flex items-center gap-2 ${isListening ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
          {isListening ? <TrendingUp className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
          <span className="text-sm font-medium">{isListening ? 'Live Updates Active' : 'Connecting...'}</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="text-slate-400 text-sm mb-1">Total Customers</div>
          <div className="text-3xl font-bold text-white">{displayStats.totalCustomers.toLocaleString()}</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="text-slate-400 text-sm mb-1">Service Accounts</div>
          <div className="text-3xl font-bold text-white">{displayStats.totalServiceAccounts.toLocaleString()}</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="text-slate-400 text-sm mb-1">Locations</div>
          <div className="text-3xl font-bold text-white">{displayStats.totalLocations.toLocaleString()}</div>
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="text-slate-400 text-sm mb-1">Recent (5 min)</div>
          <div className="text-3xl font-bold text-blue-400">{displayStats.recentImports.toLocaleString()}</div>
        </div>
      </div>

      {/* Sync Status */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-white mb-4">Sync Status by Company</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Company</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Status</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Last Sync</th>
                <th className="text-right text-sm font-medium text-slate-400 pb-3">Total Synced</th>
              </tr>
            </thead>
            <tbody>
              {syncStates.map((state, index) => (
                <tr key={state.company_id} className={index !== syncStates.length - 1 ? 'border-b border-slate-800' : ''}>
                  <td className="py-3 text-white">{state.company_id}</td>
                  <td className="py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border ${getStatusColor(state.last_sync_status)}`}>
                      {getStatusIcon(state.last_sync_status)}
                      {state.last_sync_status}
                    </span>
                  </td>
                  <td className="py-3 text-slate-400 text-sm">
                    {state.last_incremental_sync ? new Date(state.last_incremental_sync).toLocaleString() : 'Never'}
                  </td>
                  <td className="py-3 text-right text-white">{state.total_customers_synced?.toLocaleString() || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Live Activity Feed */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Live Activity Feed</h2>
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-slate-900 z-10">
              <tr className="border-b border-slate-800">
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Time</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Type</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Action</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Name/Details</th>
                <th className="text-left text-sm font-medium text-slate-400 pb-3">Company</th>
              </tr>
            </thead>
            <tbody>
              {activity.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-slate-400">
                    Waiting for activity... (System syncs every 5 minutes)
                  </td>
                </tr>
              ) : (
                activity.map((item, index) => (
                  <tr
                    key={`${item.id}-${index}`}
                    className={`border-b border-slate-800 ${index === 0 ? 'animate-fade-in' : ''}`}
                  >
                    <td className="py-3 text-slate-400 text-sm">{new Date(item.timestamp).toLocaleTimeString()}</td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${item.type === 'customer' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-slate-700 text-slate-300 border border-slate-600'}`}>
                        {getTypeIcon(item.type)}
                        {item.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${item.action === 'created' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                        {item.action}
                      </span>
                    </td>
                    <td className="py-3 text-white text-sm">{item.name}</td>
                    <td className="py-3 text-slate-400 text-sm">{item.company_id}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            background-color: rgba(76, 175, 80, 0.2);
          }
          to {
            background-color: transparent;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default SuccessWareActivityMonitorPage;
